import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useWorkspaceStore } from '@/hooks/state';
import { nextPageVisit, pageReadySample, type PageVisit, type ReadyMetricName, type ReadyOutcome } from '@/lib/pageReadyTiming';
import { trackDataReadyMetric } from '@/lib/rum';

const PageTimingContext = createContext<PageVisit | null>(null);

/** Lives above the gate and lazy routes so their wait is included. */
export function PageTimingProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const workspaceId = useWorkspaceStore((state) => state.workspaceId);
  const [visit, setVisit] = useState(() => nextPageVisit(null, location.key, location.pathname, workspaceId, 0));
  if (visit.key !== location.key || visit.workspaceId !== workspaceId) {
    // Reset during render: children commit with the new visit before effects run.
    setVisit(nextPageVisit(visit, location.key, location.pathname, workspaceId, performance.now()));
  }
  return <PageTimingContext.Provider value={visit}>{children}</PageTimingContext.Provider>;
}

export function useDataReadyTiming(name: ReadyMetricName, outcome: ReadyOutcome | null) {
  const visit = useContext(PageTimingContext);
  useEffect(() => {
    if (!visit || !outcome || visit.reported.has(name)) return;
    let secondFrame: number | undefined;
    const firstFrame = requestAnimationFrame(() => {
      secondFrame = requestAnimationFrame(() => {
        const sample = pageReadySample(visit, name, outcome, performance.now());
        if (sample) trackDataReadyMetric(sample);
      });
    });
    return () => {
      cancelAnimationFrame(firstFrame);
      if (secondFrame !== undefined) cancelAnimationFrame(secondFrame);
    };
  }, [visit, name, outcome]);
}
